import { ref, inject, computed, unref, provide } from 'vue';
import { useLockMeta } from './compositions/component-lib.esm3.js';
import { getMeta } from '../utils/component-lib.esm13.js';
import { canAddUTMParams, appendUTMParams } from '../utils/component-lib.esm4.js';
import { useEmbedOptions } from '../compositions/component-lib.esm.js';
import { EVENTS } from '../config/component-lib.esm.js';
function useLink(props, _ref) {
  let {
    emit
  } = _ref;
  const expand = ref(null);
  const isOpen = ref(false);
  const highlight = ref(null);
  const profile = inject('profile');
  const profileRef = computed(() => unref(profile));
  const link = computed(() => props.link);
  const id = computed(() => link.value._id.$oid);
  const linkType = computed(() => link.value.type);
  const lockMeta = computed(() => {
    return link.value.locked || {};
  });
  const isFreePlan = computed(() => {
    return unref(profile).isFreePlan;
  });
  const {
    isLocked,
    lockedUrl
  } = useLockMeta(lockMeta);
  let hasBeenUnlocked = ref(false);
  const currentHighlight = computed(() => {
    return highlight.value || link.value.highlight;
  });
  const linkBaseUrl = computed(() => {
    if (isLocked.value && !hasBeenUnlocked.value) return null;
    let url = link.value.url;
    if (lockedUrl.value) url = lockedUrl.value;
    return url;
  });
  const enableUtmParameters = computed(() => {
    return getMeta('enable_utm_parameters', false, profileRef.value.meta);
  });
  const utmMedium = computed(() => {
    return getMeta('utm_medium', null, profileRef.value.meta);
  });
  const utmSource = computed(() => {
    return getMeta('utm_source', null, profileRef.value.meta);
  });
  const linkUrl = computed(() => {
    if (enableUtmParameters.value && canAddUTMParams(linkBaseUrl.value)) {
      return appendUTMParams(linkBaseUrl.value, utmMedium.value, utmSource.value, link.value.label);
    }
    return linkBaseUrl.value;
  });
  const imageUrl = computed(() => {
    const media = getMeta('media', null, link.value.meta);
    if (media) return media.url || media.image;
    if (link.value.custom_media && link.value.custom_media.length) return link.value.custom_media[0].url;
    return link.value.image;
  });
  const embedType = computed(() => link.value.video_embed_type);
  const [isEmbedLink, embedOptions, embedId] = useEmbedOptions({
    embedType,
    url: linkBaseUrl
  });
  const isContactDetailsLink = computed(() => linkType.value === 'contact-details');
  const isShopLink = computed(() => linkType.value === 'shop');
  const isExpandableLink = computed(() => {
    return isShopLink.value || isContactDetailsLink.value || isEmbedLink.value || isLocked.value && !hasBeenUnlocked.value;
  });
  const showLockedTemplate = computed(() => {
    if (isLocked.value && (isEmbedLink.value || isContactDetailsLink.value || isShopLink.value) && hasBeenUnlocked.value) return false;else if (isLocked.value) return true;
    return false;
  });
  const variant = computed(() => {
    const settings = profileRef.value.theme ? profileRef.value.theme : profileRef.value;
    if (props.variant) return props.variant;
    if (link.value.type === 'delimiter') {
      return settings.site_divider_style;
    }
    return settings.site_button_style;
  });
  const btnProps = computed(() => {
    return {
      url: linkUrl.value,
      label: props.link.label,
      imageUrl: imageUrl.value,
      imageStyle: props.imageStyle || profileRef.value.site_button_image_style,
      highlight: currentHighlight.value,
      variant: variant.value,
      size: props.btnSize || profileRef.value.site_button_size
    };
  });
  function handleUnlock(data) {
    hasBeenUnlocked.value = true;
    emit('event', EVENTS.UNLOCKED);
    lockedUrl.value = data.link_url;
    // Close the expand if it's open, unless its an embedded link or a contact-details link
    if (!isEmbedLink.value && !isContactDetailsLink.value && !isShopLink.value) {
      isOpen.value = false;
      // Open the url in a new tab
      const tab = window.open(linkUrl.value, '_blank');
      try {
        tab.focus();
      } catch (e) {
        window.location = linkUrl.value;
      }
    }
  }
  function handleClick() {
    if (isExpandableLink.value) {
      if (!isOpen.value) emit('click');
      isOpen.value = !isOpen.value;
    } else emit('click');
  }
  function setHighlight(val) {
    highlight.value = val;
  }
  const linkData = {
    id,
    linkUrl,
    linkType,
    imageUrl,
    hasBeenUnlocked,
    lockMeta,
    isLocked,
    showLockedTemplate,
    isEmbedLink,
    embedOptions,
    embedId,
    expand,
    isOpen,
    handleClick,
    handleUnlock,
    setHighlight,
    btnProps,
    variant,
    isFreePlan,
    profile: profileRef,
    enableUtmParameters,
    utmMedium,
    utmSource
  };
  provide('link', {
    link,
    ...linkData
  });
  return linkData;
}
export { useLink };